import { v4 as uuidv4 } from 'uuid';
import {
  CreateAddressRequest,
  CreateAddressRequestRhResponse,
  LatLng,
  Location,
  SavedLocationDtoRH
} from './models/locations';
import {
  createAddressRequestToRHRequest,
  dtoNearbyRHToSearchLocations,
  dtoRHToFirstNearbyLocation,
  dtoRhToSavedLocation,
  dtoRHToSearchLocation,
  dtoToCities,
  nearbyDtoToSearchLocation,
  reverseGeocodeDtoToSearchLocation,
  transformDirectionsResponse
} from './transformers/locations';
import { navigationsClient, api, sagwClient, bifrostLocationsClient } from './clients';
import { MAX_FETCH_LOCATIONS_SIZE } from '../utils/constants';

export function searchForLocations(query: string, latLng: LatLng) {
  return sagwClient
    .get('/locations/v1/search', {
      headers: {
        'X-CLIENT-NAME': 'Express'
      },
      params: {
        fieldType: 1,
        device: 'PORTAL',
        lang: 'en',
        latitude: latLng.lat,
        longitude: latLng.lng,
        param: query
      }
    })
    .then(dtoRHToSearchLocation);
}

type NavigationHeadersData = {
  bookingId?: number;
  userId?: number;
};

export function getHeaders(headersData: NavigationHeadersData) {
  const headers: { [key: string]: string } = {
    'X-Request-ID': uuidv4()
  };

  if (headersData?.bookingId) {
    headers['X-CAREEM-BOOKING-ID'] = headersData.bookingId.toString();
  }
  if (headersData?.userId) {
    headers['X-CAREEM-USER-ID'] = headersData.userId.toString();
  }
  return headers;
}

export type DistanceProvider = 'google' | 'osrm';

export function getDirections(
  pickup: LatLng,
  dropOff: LatLng,
  headersData: NavigationHeadersData,
  requestData: { steps?: boolean; annotations?: boolean; overview?: boolean } = {},
  provider: DistanceProvider = 'osrm'
) {
  const request = {
    coordinates: [
      {
        latitude: pickup.lat,
        longitude: pickup.lng
      },
      {
        latitude: dropOff.lat,
        longitude: dropOff.lng
      }
    ],
    ...requestData
  };

  const headers = { ...getHeaders(headersData), 'X-CAREEM-USECASE': 'mot-express-portal-check-driving-distance' };

  return navigationsClient.post('/directions/v1/driving', JSON.stringify(request), {
    headers,
    params: {
      provider
    }
  });
}

export function getGoogleMapDirections(
  pickup: LatLng,
  dropOff: LatLng,
  headersData: NavigationHeadersData,
  provider?: DistanceProvider
) {
  return getDirections(
    pickup,
    dropOff,
    headersData,
    {
      steps: true,
      annotations: true,
      overview: true
    },
    provider
  ).then(transformDirectionsResponse(pickup, dropOff));
}

export function fetchCities(filterInactiveCities = false) {
  return api
    .get(`/v2/cities`, {
      params: {
        status_check: filterInactiveCities,
        includes: ['support']
      }
    })
    .then(dtoToCities);
}

export function getNearbyLocationMOT(latLng: LatLng) {
  return api
    .get('/v1/addresses/nearby', {
      params: {
        lat: latLng.lat,
        lng: latLng.lng,
        first: 1
      }
    })
    .then(nearbyDtoToSearchLocation);
}

export function getNearbyLocation(latLng: LatLng) {
  return sagwClient
    .get('/locations/v1/nearby', {
      headers: {
        'X-CLIENT-NAME': 'Express'
      },
      params: {
        latitude: latLng.lat,
        longitude: latLng.lng,
        lang: 'en'
      }
    })
    .then(dtoRHToFirstNearbyLocation);
}

export function getNearbyLocationsList(latLng: LatLng) {
  return sagwClient
    .get('/locations/v1/nearby', {
      headers: {
        'X-CLIENT-NAME': 'Express'
      },
      params: {
        latitude: latLng.lat,
        longitude: latLng.lng,
        lang: 'en'
      }
    })
    .then(dtoNearbyRHToSearchLocations);
}

export function fetchPickupLocations(): Promise<Location[]> {
  return sagwClient
    .get<SavedLocationDtoRH[]>('/locations/v1/saved', {
      params: {
        size: MAX_FETCH_LOCATIONS_SIZE
      }
    })
    .then(dtoRhToSavedLocation);
}

export function createAddress(request: CreateAddressRequest): Promise<CreateAddressRequestRhResponse> {
  return sagwClient.post('/locations/v1/saved', createAddressRequestToRHRequest(request));
}

export function removeAddress(addressId: number) {
  return bifrostLocationsClient.delete(`/v1/saved/${addressId}`);
}

export function getReverseGeocode(latLng: LatLng) {
  return bifrostLocationsClient
    .get('/v1/reverse-geocode', {
      params: {
        latitude: latLng.lat,
        longitude: latLng.lng,
        lang: 'en'
      }
    })
    .then(reverseGeocodeDtoToSearchLocation);
}
